/**
 * arbi skills list                  — list user-invocable skills
 * arbi skills show <name>           — print a skill's SKILL.md body
 * arbi skills install <source>      — upload a SKILL.md from a URL or local path
 *
 * Skills are workspace documents (``wp_type='skill'``) parsed by the
 * backend's frontmatter loader. This CLI surface is the parity layer
 * for the TUI's ``/skills`` / ``/skill view`` commands and the React
 * UI's slash menu — all three frontends read the same
 * ``GET /v1/assistant/skills`` endpoint via the SDK.
 *
 * The CLI uses a workspace context (not just auth) because skills are
 * scoped to the active workspace's encryption key — the same reason
 * the backend endpoint takes a workspace-context dependency.
 */

import { readFileSync, statSync } from 'node:fs'
import { basename } from 'node:path'
import { Command } from 'commander'
import { assistant, documents } from '@arbidocs/sdk'
import { runAction, resolveWorkspace, printTable } from '../helpers.js'
import { bold, dim } from '../output.js'

/**
 * Parse the frontmatter block of a SKILL.md to extract a slug.
 *
 * Returns ``null`` if no frontmatter, no ``name`` field, or the file
 * doesn't start with ``---``. We only need ``name`` — the backend
 * loader handles the rest. ``--slug`` overrides this entirely.
 *
 * Exported for unit tests.
 */
export function extractSlugFromFrontmatter(body: string): string | null {
  // Must open with ``---\n`` exactly. Windows line endings (\r\n) are
  // tolerated by stripping the carriage return before matching.
  const normalized = body.replace(/\r\n/g, '\n')
  if (!normalized.startsWith('---\n')) return null

  const end = normalized.indexOf('\n---', 4)
  if (end < 0) return null
  const block = normalized.slice(4, end)

  // Minimal YAML: ``name: <value>`` on its own line. Doesn't handle
  // quoted strings or block scalars — but the SKILL.md convention
  // already requires a one-line ``name`` field, so this matches every
  // file the backend's parser accepts.
  for (const line of block.split('\n')) {
    const m = /^name:\s*(.+?)\s*$/.exec(line)
    if (m) {
      // Slugify: lowercase, replace whitespace and underscores with
      // dashes, drop chars that aren't a-z/0-9/dash. Matches the
      // backend's ``SkillManager._slug`` (str-to-slug) behavior so
      // the user types the same ``/<slug>`` either way.
      return m[1]
        .toLowerCase()
        .replace(/['"]/g, '')
        .replace(/[_\s]+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    }
  }
  return null
}

/**
 * Resolve a skill source (URL or local file) to its body text.
 *
 * - ``http://`` or ``https://`` → ``fetch`` it. We don't follow
 *   ``data:`` URIs or anything else; keep the surface obvious.
 * - Anything else is treated as a local path. We refuse directories
 *   here because the upload endpoint takes a single file — if the
 *   user wants to install a multi-file skill, they should use
 *   ``arbi upload --folder skills/<slug> --wp-type skill``.
 *
 * Exported for unit tests.
 */
export async function loadSkillSource(source: string): Promise<string> {
  if (source.startsWith('http://') || source.startsWith('https://')) {
    const res = await fetch(source)
    if (!res.ok) {
      throw new Error(`Failed to fetch ${source}: HTTP ${res.status} ${res.statusText}`)
    }
    return await res.text()
  }

  const stat = statSync(source)
  if (stat.isDirectory()) {
    throw new Error(
      `${source} is a directory. Pass a single SKILL.md file, or use ` +
        '``arbi upload --folder skills/<slug> --wp-type skill`` for multi-file skills.'
    )
  }
  void basename // reserved for future ``--name-from-file`` heuristic
  return readFileSync(source, 'utf8')
}

export function registerSkillsCommand(program: Command): void {
  const skills = program.command('skills').description('Skills: list, show')

  skills
    .command('list')
    .description('List user-invocable skills in the active workspace')
    .option('--json', 'Output as JSON')
    .option('--include-hidden', 'Include skills with user-invocable: false')
    .action((opts: { json?: boolean; includeHidden?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const list = await assistant.listSkills(arbi, {
          includeHidden: opts.includeHidden,
        })

        if (opts.json) {
          console.log(JSON.stringify(list, null, 2))
          return
        }

        if (list.length === 0) {
          process.stderr.write(
            'No skills in this workspace yet. Upload a SKILL.md with wp_type=skill to add one.\n'
          )
          return
        }

        // Same table layout as ``arbi contacts list``: ID-ish first
        // column, name, then variable-width detail. Slug is the
        // ``/<slug>`` identifier the user types in the TUI.
        printTable(
          [
            { header: 'SLUG', width: 20, value: (r) => (r.slug as string) ?? '' },
            { header: 'NAME', width: 24, value: (r) => (r.name as string) ?? '' },
            {
              header: 'TYPE',
              width: 10,
              value: (r) => (r.skill_type as string) ?? 'markdown',
            },
            {
              header: 'ARGS',
              width: 20,
              value: (r) => (r.arg_hint as string) ?? '',
            },
            {
              header: 'DESCRIPTION',
              width: 40,
              value: (r) => (r.description as string) ?? '',
            },
          ],
          list as unknown as Record<string, unknown>[]
        )
      })()
    )

  skills
    .command('install <source>')
    .description(
      'Install a skill from a URL or local path (SKILL.md). Slug derived from frontmatter unless --slug is given.'
    )
    .option('--slug <slug>', 'Override the slug (default: derived from frontmatter name)')
    .option('--json', 'Output the upload result as JSON')
    .action((source: string, opts: { slug?: string; json?: boolean }) =>
      runAction(async () => {
        const ctx = await resolveWorkspace()
        const authHeaders = {
          baseUrl: ctx.config.baseUrl,
          accessToken: ctx.accessToken,
        }

        // Source resolution: anything starting with http(s) is fetched
        // over the network; everything else is read from disk. We
        // don't try to be clever about ``file://`` URIs — keep the
        // surface small and obvious. Users can pipe through curl if
        // they need anything fancier.
        const body = await loadSkillSource(source)

        // Slug precedence: explicit --slug → frontmatter name →
        // error. We never silently pick something from the filename;
        // a SKILL.md from a tarball might be called whatever, and we
        // don't want the slug to depend on directory layout.
        const slug =
          opts.slug ??
          extractSlugFromFrontmatter(body) ??
          (() => {
            process.stderr.write(
              'Could not derive slug: no --slug flag and no ``name:`` in frontmatter.\n' +
                'Add a frontmatter block like:\n\n  ---\n  name: my-skill\n  description: …\n  ---\n\n' +
                'or pass ``--slug my-skill`` explicitly.\n'
            )
            process.exit(1)
          })()

        // Upload via the same path as ``arbi upload --wp-type skill
        // --folder skills/<slug>``. We always use the plain
        // ``uploadFile`` path (not direct-to-MinIO) because SKILL.md
        // files are tiny — a few KB at most — and the streaming
        // overhead of the direct path doesn't pay off here.
        const blob = new Blob([body], { type: 'text/markdown' })
        const result = await documents.uploadFile(authHeaders, blob, 'SKILL.md', {
          folder: `skills/${slug}`,
          wpType: 'skill',
        })

        if (opts.json) {
          console.log(JSON.stringify({ slug, ...result }, null, 2))
          return
        }

        console.log(bold(`Installed skill: ${slug}`))
        console.log(dim(`source: ${source}`))
        console.log(dim(`folder: skills/${slug}`))
        console.log(
          dim('Backend frontmatter loader picks it up on the next /v1/assistant/skills call.')
        )
      })()
    )

  skills
    .command('show <slug>')
    .description("Show a skill's SKILL.md body (read-only)")
    .option('--json', 'Output as JSON (includes metadata)')
    .action((slug: string, opts: { json?: boolean }) =>
      runAction(async () => {
        const ctx = await resolveWorkspace()
        const list = await assistant.listSkills(ctx.arbi, { includeHidden: true })
        const skill = list.find((s) => s.slug === slug || s.name === slug)
        if (!skill) {
          process.stderr.write(
            `No skill matching "${slug}" in this workspace. Try \`arbi skills list\`.\n`
          )
          process.exit(1)
        }

        // Fetch the skill body via the regular document-download
        // endpoint — the SKILL.md is just a markdown file in the
        // workspace. We can't use ``getParsedContent`` here because
        // the marker parsing pipeline only runs for source documents;
        // skills typically skip it (or 422 if it tried).
        const authHeaders = {
          baseUrl: ctx.config.baseUrl,
          accessToken: ctx.accessToken,
        }
        const dlRes = await documents.downloadDocument(authHeaders, skill.doc_ext_id)
        const body = dlRes.ok ? await dlRes.text() : ''

        if (opts.json) {
          console.log(JSON.stringify({ ...skill, body }, null, 2))
          return
        }

        // Pretty text output: header line per metadata field, then the
        // body verbatim. Matches the TUI's ``/skill view`` rendering.
        console.log(bold(`${skill.name} (${skill.slug})`))
        if (skill.description) console.log(dim(skill.description))
        if (skill.arg_hint) console.log(dim(`args: ${skill.arg_hint}`))
        console.log(dim(`type: ${skill.skill_type}  doc: ${skill.doc_ext_id}`))
        console.log('')
        console.log(body)
      })()
    )
}
